/**
 * HashMedora Analytics API
 * Client-side placeholder for global platform analytics.
 * Uses localStorage. Replace with backend calls when ready.
 */

import { BigNumber, ethers } from 'ethers';
import { storage } from './storage';
import { GAMES, GAME_IDS } from '../data/games';
import { coreVaultAddress, gameContractAddresses, getGameContractAddress } from '../utils/addressHelpers.ts';
import { readContract } from 'wagmi/actions';
import { stringToHex } from 'viem';
import { config } from '../config.jsx';
import gameAbi from '../config/gameAbi.json';
import { formatGameNumber, formatSoldTicketsWithTotal } from './gameApi';
import {
  BOUGHT_EVENT_TOPIC0,
  ETHERSCAN_GAME_LOGS_FROM_BLOCK,
  ETHERSCAN_GET_LOGS_SOURCE,
  ETHERSCAN_WALLET_TOPIC_INDEX,
  fetchEtherscanBscMainNetGetLogs,
  getDefaultDateRange,
  getDefaultLastDrawDateRange,
  GET_WINNER_DATA_API_URL,
  GET_BOUGHT_DATA_API_URL,
  isEtherscanNoRecordsFoundResponse,
  parseFilterTimestampFrom,
  parseFilterTimestampTo,
  resolveBlockRangeFromDates,
  VAULT_WINNER_EVENT_TOPIC0,
} from '../utils/constants.ts';

export { getDefaultDateRange, getDefaultLastDrawDateRange, resolveBlockRangeFromDates } from '../utils/constants.ts';

/** `topics[1]` for indexed address: 32-byte word, last 20 bytes = address. */
function addressFromTopic(topic) {
  if (typeof topic !== 'string' || topic.length < 42) return null;
  return `0x${topic.slice(-40).toLowerCase()}`;
}

/** Address in ABI-encoded `data` at word index (right-padded in 32 bytes). */
function addressFromDataWord(data, wordIndex) {
  if (typeof data !== 'string' || !data.startsWith('0x')) return null;
  const hex = data.slice(2);
  const start = wordIndex * 64;
  const word = hex.slice(start, start + 64);
  if (!word || word.length < 64) return null;
  return addressFromTopic(`0x${word}`);
}

/** One ABI-encoded uint256 word at `wordIndex` in non-indexed `data` (32-byte words). */
function uint256WordFromData(data, wordIndex = 0) {
  if (typeof data !== 'string' || !data.startsWith('0x')) return BigNumber.from(0);
  const hex = data.slice(2);
  const start = wordIndex * 64;
  const word = hex.slice(start, start + 64);
  if (!word || word.length < 64) return BigNumber.from(0);
  return BigNumber.from(`0x${word}`);
}

/** First word in `data` (backwards compatible for vault-style logs). */
function uint256FromLogData(data) {
  return uint256WordFromData(data, 0);
}

function parseHexInt(value) {
  if (value == null || value === '') return 0;
  if (typeof value === 'string' && value.startsWith('0x')) return parseInt(value, 16);
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function logTimestampMs(log) {
  if (!log?.timeStamp) return 0;
  return parseHexInt(log.timeStamp) * 1000;
}

/** Full 32-byte ABI words in `data` (after 0x). */
function abiDataWordCount(data) {
  if (typeof data !== 'string' || !data.startsWith('0x')) return 0;
  const hex = data.slice(2);
  if (hex.length === 0 || hex.length % 64 !== 0) return 0;
  return hex.length / 64;
}

/** Map `Bought` event `topics[1]` (bytes32 gameType) to UI game short name. */
function gameNameFromGameTypeTopic(gameTypeTopic) {
  if (typeof gameTypeTopic !== 'string') return 'Unknown';
  const t = gameTypeTopic.toLowerCase();
  for (const gameId of GAME_IDS) {
    const idHex = stringToHex(gameId, { size: 32 }).toLowerCase();
    if (idHex === t) return GAMES[gameId]?.shortName || gameId;
  }
  return 'Unknown';
}

/** Map `topics[1]` bytes32 game id slot to `GAMES` key (e.g. `strike`). */
function gameIdFromGameTypeTopic(gameTypeTopic) {
  if (typeof gameTypeTopic !== 'string') return null;
  const t = gameTypeTopic.toLowerCase();
  for (const gameId of GAME_IDS) {
    const idHex = stringToHex(gameId, { size: 32 }).toLowerCase();
    if (idHex === t) return gameId;
  }
  return null;
}

/** e.g. `Strike-1` for table Game column. */
export function formatGameRoundLabel(gameName, roundId) {
  const name = gameName || 'Unknown';
  if (roundId === undefined || roundId === null || roundId === '') return name;
  return `${name}-${roundId}`;
}

/**
 * Decode game contract `Bought` log (Etherscan getLogs shape).
 * Non-indexed `data` is three 32-byte words: roundId, price (total USDT in wei), picked number.
 * Event: Bought(bytes32 indexed gameType, uint256 roundId, address indexed buyer, uint256 price, uint256 number)
 */
export function decodeBoughtLog(log) {
  const topics = log?.topics;
  if (!Array.isArray(topics) || topics.length < 3) return null;
  const buyer = addressFromTopic(topics[2]);
  if (!buyer) return null;
  const gameName = gameNameFromGameTypeTopic(topics[1]);
  const roundId = uint256WordFromData(log.data, 0);
  /** Total USDT for this purchase (18 decimals), second word only. */
  const priceWei = uint256WordFromData(log.data, 1);
  /** On-chain pick id / lottery number for this ticket (third word — not a ticket count). */
  const pickNumberBn = uint256WordFromData(log.data, 2);
  const gameId = gameIdFromGameTypeTopic(topics[1]);
  const txHash = log.transactionHash || log.hash || null;
  const blockNumber = parseHexInt(log.blockNumber);
  const logIndex = parseHexInt(log.logIndex);
  const sortKey = blockNumber * 100000 + logIndex;
  let totalDepositedUsdt = 0;
  try {
    totalDepositedUsdt = parseFloat(ethers.utils.formatUnits(priceWei, 18));
  } catch {
    totalDepositedUsdt = 0;
  }
  const ridStr = roundId.toString();
  const pickedNumberStr = pickNumberBn.toString();
  return {
    key: txHash ? `${txHash}-${log.logIndex ?? logIndex}` : `log-${sortKey}`,
    gameName,
    gameId,
    roundId: ridStr,
    gameRoundLabel: formatGameRoundLabel(gameName, ridStr),
    userAddress: buyer,
    userDisplay: `${buyer.slice(0, 6)}…${buyer.slice(-4)}`,
    totalDepositedUsdt,
    priceWeiStr: priceWei.toString(),
    pickedNumberStr,
    timestampMs: logTimestampMs(log),
    txHash,
    sortKey,
  };
}

/**
 * One `buyTickets` tx can emit several `Bought` logs (one per picked number).
 * Merge rows that share the same `txHash` for tables keyed by transaction.
 */
export function mergeBoughtRowsByTxHash(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const withTx = rows.filter((r) => r?.txHash && typeof r.txHash === 'string');
  const withoutTx = rows.filter((r) => !r?.txHash);
  const groups = new Map();
  for (const r of withTx) {
    const k = r.txHash.toLowerCase();
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(r);
  }
  const merged = [];
  for (const [, group] of groups) {
    group.sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0));
    const first = group[0];
    const pickedNumberStrs = group
      .map((g) => g.pickedNumberStr)
      .filter((x) => x != null && String(x) !== '');
    let sumUsdt = 0;
    let sumWei = BigNumber.from(0);
    let maxSort = 0;
    let maxTs = 0;
    for (const g of group) {
      sumUsdt += Number(g.totalDepositedUsdt) || 0;
      try {
        sumWei = sumWei.add(BigNumber.from(g.priceWeiStr || '0'));
      } catch {
        /* ignore */
      }
      maxSort = Math.max(maxSort, g.sortKey || 0);
      maxTs = Math.max(maxTs, g.timestampMs || 0);
    }
    merged.push({
      ...first,
      key: first.txHash,
      pickedNumberStr: pickedNumberStrs[0] ?? first.pickedNumberStr,
      pickedNumberStrs,
      totalDepositedUsdt: sumUsdt,
      priceWeiStr: sumWei.toString(),
      sortKey: maxSort,
      timestampMs: maxTs,
    });
  }
  return [...merged, ...withoutTx];
}

function bytes32WordHexFromData(data, wordIndex) {
  if (typeof data !== 'string' || !data.startsWith('0x')) return null;
  const hex = data.slice(2);
  const start = wordIndex * 64;
  const word = hex.slice(start, start + 64);
  if (!word || word.length < 64) return null;
  return `0x${word}`.toLowerCase();
}

/**
 * Decode core vault payout log (`VAULT_WINNER_EVENT_TOPIC0`).
 * `topics[1]` = indexed winner. `data`: reward (wei), gameType (bytes32), roundId, optional winning pick number.
 */
export function decodeVaultWinnerLog(log) {
  const topics = log?.topics;
  if (!Array.isArray(topics) || topics.length < 2) return null;
  const t0 = topics[0]?.toLowerCase();
  if (t0 !== VAULT_WINNER_EVENT_TOPIC0.toLowerCase()) return null;

  const wordCount = abiDataWordCount(log.data);
  if (wordCount < 3) return null;

  const walletAddress = addressFromTopic(topics[1])?.toLowerCase();
  if (!walletAddress) return null;

  const rewardWei = uint256WordFromData(log.data, 0);
  const gameTypeHex = bytes32WordHexFromData(log.data, 1);
  if (!gameTypeHex) return null;

  const gameName = gameNameFromGameTypeTopic(gameTypeHex);
  const gameId = gameIdFromGameTypeTopic(gameTypeHex);
  const roundId = uint256WordFromData(log.data, 2);
  const roundIdStr = roundId.toString();
  const pickedNumberStr =
    wordCount >= 4 ? uint256WordFromData(log.data, 3).toString() : null;

  if (!rewardWei || rewardWei.isZero()) return null;

  let prize;
  try {
    prize = parseFloat(ethers.utils.formatUnits(rewardWei, 18));
    if (prize > 1) {
      prize = prize * 0.9;
    }
  } catch {
    return null;
  }

  const txHash = log.transactionHash || log.hash || null;
  const blockNumber = parseHexInt(log.blockNumber);
  const logIndex = parseHexInt(log.logIndex);
  const sortKey = blockNumber * 100000 + logIndex;

  return {
    walletAddress,
    displayName: `${walletAddress.slice(0, 6)}…${walletAddress.slice(-4)}`,
    gameName,
    gameId,
    roundId: roundIdStr,
    gameRoundLabel: formatGameRoundLabel(gameName, roundIdStr),
    prize,
    rewardWeiStr: rewardWei.toString(),
    pickedNumberStr,
    sortKey,
    timestampMs: logTimestampMs(log),
    txHash,
  };
}

const GLOBAL_STATS_CACHE_TTL_MS = 5 * 60 * 1000;
const VAULT_WINNER_LOGS_CACHE_TTL_MS = 5 * 60 * 1000;
const BOUGHT_LOGS_CACHE_TTL_MS = 5 * 60 * 1000;
const VAULT_WINNER_LOGS_STORAGE_KEY = 'vault_winner_logs_all';
const BOUGHT_LOGS_STORAGE_KEY = 'bought_logs_all';
const ETHERSCAN_PAGE_MAX_RETRIES = 5;
const ETHERSCAN_LOGS_FETCH_MAX_RETRIES = 3;

function isEtherscanTransientError(json) {
  if (!json || json == null) return true;
  if (json.noRecordsFound || isEtherscanNoRecordsFoundResponse(json)) return false;
  if (json.status === '0' || json.status === 0) return true;
  if (typeof json.result === 'string') return true;
  const msg = typeof json.message === 'string' ? json.message : '';
  return /server busy|rate limit|max calls|too many|timeout|try again|temporarily|unexpected|notok/i.test(msg);
}

function parseEtherscanGetLogsPageResult(json) {
  if (json?.noRecordsFound || isEtherscanNoRecordsFoundResponse(json)) {
    return { logs: [], ok: true, noRecords: true };
  }
  if (isEtherscanTransientError(json)) {
    return { logs: [], ok: false, error: new Error(json?.message || String(json?.result ?? 'Etherscan error')) };
  }
  if (!Array.isArray(json.result)) {
    return { logs: [], ok: false, error: new Error('Unexpected Etherscan getLogs response') };
  }
  return { logs: json.result, ok: true, noRecords: false };
}

/**
 * Fetch one Etherscan getLogs page with retries for rate limits and error responses.
 */
async function fetchEtherscanGetLogsPage({
  address,
  topic0,
  page,
  offset,
  fromBlock,
  toBlock,
  source = ETHERSCAN_GET_LOGS_SOURCE.ANALYTICS,
  walletAddress,
  walletTopicIndex,
}) {
  let lastError = null;
  for (let attempt = 0; attempt < ETHERSCAN_PAGE_MAX_RETRIES; attempt += 1) {
    try {
      const json = await fetchEtherscanBscMainNetGetLogs({
        address,
        topic0,
        page,
        offset,
        fromBlock,
        toBlock,
        source,
        walletAddress,
        walletTopicIndex,
      });

      const parsed = parseEtherscanGetLogsPageResult(json);
      if (parsed.noRecords) return parsed;
      if (!parsed.ok) {
        lastError = parsed.error;
        continue;
      }
      return parsed;
    } catch (e) {
      lastError = e;
    }
  }

  return { logs: [], ok: false, error: lastError };
}

/** Re-fetch a page when Etherscan returns a clean empty array but more data is expected. */
async function fetchEtherscanGetLogsPageRetryEmpty({
  address,
  topic0,
  page,
  offset,
  fromBlock,
  toBlock,
  source = ETHERSCAN_GET_LOGS_SOURCE.ANALYTICS,
  walletAddress,
  walletTopicIndex,
}) {
  let result = await fetchEtherscanGetLogsPage({
    address,
    topic0,
    page,
    offset,
    fromBlock,
    toBlock,
    source,
    walletAddress,
    walletTopicIndex,
  });
  if (result.noRecords || !result.ok || result.logs.length > 0) return result;

  for (let attempt = 1; attempt < ETHERSCAN_PAGE_MAX_RETRIES; attempt += 1) {
    result = await fetchEtherscanGetLogsPage({
      address,
      topic0,
      page,
      offset,
      fromBlock,
      toBlock,
      source,
      walletAddress,
      walletTopicIndex,
    });
    if (!result.ok) continue;
    if (result.logs.length > 0) return result;
  }

  return result;
}

let vaultWinnerLogsInflight = null;
let vaultWinnerLogsCache = { logs: null, fetchedAt: 0 };
let boughtLogsInflight = null;
let boughtLogsCache = { logs: null, fetchedAt: 0 };
let globalStatsInflight = null;

function readStoredLogsCache(storageKey, ttlMs) {
  const cached = storage.get(storageKey);
  if (!cached?.logs || !cached?.fetchedAt) return null;
  if (Date.now() - cached.fetchedAt > ttlMs) return null;
  return cached.logs;
}

function writeStoredLogsCache(storageKey, logs) {
  if (!Array.isArray(logs) || logs.length === 0) return;
  const payload = { logs, fetchedAt: Date.now() };
  storage.set(storageKey, payload);
}

/** Return cached platform stats synchronously (for instant UI). */
export function getCachedGlobalStats() {
  const cached = storage.get('global_stats');
  if (!cached?.updatedAt) return null;
  if (Date.now() - cached.updatedAt > GLOBAL_STATS_CACHE_TTL_MS) return null;
  return cached;
}

async function fetchLogsAllPagesUncached({
  address,
  topic0,
  maxPages = 50,
  label = 'getLogs',
  fromBlock,
  toBlock,
  source = ETHERSCAN_GET_LOGS_SOURCE.ANALYTICS,
  walletAddress,
  walletTopicIndex,
}) {
  const offset = 1000;
  const out = [];
  let lastPageSize = 0;
  let sawNoRecords = false;

  for (let page = 0; page < maxPages; page += 1) {
    const pageParams = {
      address,
      topic0,
      page,
      offset,
      fromBlock,
      toBlock,
      source,
      walletAddress,
      walletTopicIndex,
    };
    const suspectEmpty = page === 0 || lastPageSize >= offset;
    const { logs, ok, error, noRecords } = suspectEmpty
      ? await fetchEtherscanGetLogsPageRetryEmpty(pageParams)
      : await fetchEtherscanGetLogsPage(pageParams);

    if (!ok) {
      throw error || new Error(`Etherscan ${label} failed on page ${page} for ${address}`);
    }

    if (noRecords) {
      sawNoRecords = true;
      break;
    }

    if (logs.length === 0) break;

    lastPageSize = logs.length;
    out.push(...logs);
    if (logs.length < offset) break;
  }

  return { logs: out, confirmedEmpty: sawNoRecords };
}

async function fetchLogsAllPagesWithRetry({
  address,
  topic0,
  maxPages = 50,
  label = 'getLogs',
  fromBlock,
  toBlock,
  source = ETHERSCAN_GET_LOGS_SOURCE.ANALYTICS,
  walletAddress,
  walletTopicIndex,
}) {
  let logs = [];
  for (let attempt = 0; attempt < ETHERSCAN_LOGS_FETCH_MAX_RETRIES; attempt += 1) {
    try {
      const result = await fetchLogsAllPagesUncached({
        address,
        topic0,
        maxPages,
        label,
        fromBlock,
        toBlock,
        source,
        walletAddress,
        walletTopicIndex,
      });
      logs = result.logs;
      if (result.confirmedEmpty || logs.length > 0) return logs;
    } catch (e) {
      console.warn(`${label} attempt failed for ${address}:`, e);
      if (attempt === ETHERSCAN_LOGS_FETCH_MAX_RETRIES - 1) throw e;
    }
  }
  return logs;
}

async function fetchVaultWinnerLogsAllPagesUncached(
  maxPages = 50,
  source = ETHERSCAN_GET_LOGS_SOURCE.ANALYTICS,
  walletAddress,
) {
  const { logs } = await fetchLogsAllPagesUncached({
    address: coreVaultAddress,
    topic0: VAULT_WINNER_EVENT_TOPIC0,
    maxPages,
    label: 'vault winner getLogs',
    source,
    walletAddress,
    walletTopicIndex: walletAddress ? ETHERSCAN_WALLET_TOPIC_INDEX.VAULT_WINNER : undefined,
  });
  return logs;
}

/**
 * Paginate Etherscan `getLogs` for core vault payout events (`VAULT_WINNER_EVENT_TOPIC0`).
 * Concurrent callers share one in-flight request; results are cached briefly to avoid duplicate fetches.
 */
export async function fetchVaultWinnerLogsAllPages(
  maxPages = 50,
  source = ETHERSCAN_GET_LOGS_SOURCE.ANALYTICS,
  walletAddress,
) {
  if (walletAddress) {
    return fetchVaultWinnerLogsAllPagesUncached(maxPages, source, walletAddress);
  }

  const now = Date.now();
  if (
    vaultWinnerLogsCache.logs &&
    now - vaultWinnerLogsCache.fetchedAt < VAULT_WINNER_LOGS_CACHE_TTL_MS
  ) {
    return vaultWinnerLogsCache.logs;
  }

  const storedLogs = readStoredLogsCache(VAULT_WINNER_LOGS_STORAGE_KEY, VAULT_WINNER_LOGS_CACHE_TTL_MS);
  if (storedLogs) {
    vaultWinnerLogsCache = { logs: storedLogs, fetchedAt: Date.now() };
    return storedLogs;
  }

  if (vaultWinnerLogsInflight) {
    return vaultWinnerLogsInflight;
  }

  vaultWinnerLogsInflight = (async () => {
    try {
      let logs = [];
      for (let attempt = 0; attempt < ETHERSCAN_LOGS_FETCH_MAX_RETRIES; attempt += 1) {
        try {
          logs = await fetchVaultWinnerLogsAllPagesUncached(maxPages, source);
          if (logs.length > 0) break;
        } catch (e) {
          console.warn('fetchVaultWinnerLogsAllPages attempt failed:', e);
          if (attempt === ETHERSCAN_LOGS_FETCH_MAX_RETRIES - 1) throw e;
        }
      }

      if (logs.length > 0) {
        const fetchedAt = Date.now();
        vaultWinnerLogsCache = { logs, fetchedAt };
        writeStoredLogsCache(VAULT_WINNER_LOGS_STORAGE_KEY, logs);
      }
      return logs;
    } finally {
      vaultWinnerLogsInflight = null;
    }
  })();

  return vaultWinnerLogsInflight;
}

/** Clears cached vault winner logs so the next fetch hits Etherscan again. */
export function invalidateVaultWinnerLogsCache() {
  vaultWinnerLogsCache = { logs: null, fetchedAt: 0 };
  vaultWinnerLogsInflight = null;
  storage.remove(VAULT_WINNER_LOGS_STORAGE_KEY);
}

/** Clears cached bought logs so the next fetch hits Etherscan again. */
export function invalidateBoughtLogsCache() {
  boughtLogsCache = { logs: null, fetchedAt: 0 };
  boughtLogsInflight = null;
  storage.remove(BOUGHT_LOGS_STORAGE_KEY);
}

/** Paginate Etherscan `getLogs` for one contract address. */
async function fetchLogsAllPagesForAddress({
  address,
  topic0,
  maxPages = 50,
  fromBlock,
  toBlock,
  source = ETHERSCAN_GET_LOGS_SOURCE.ANALYTICS,
  walletAddress,
  walletTopicIndex,
}) {
  return fetchLogsAllPagesWithRetry({
    address,
    topic0,
    maxPages,
    fromBlock,
    toBlock,
    source,
    walletAddress,
    walletTopicIndex,
    label: 'getLogs',
  });
}

/** Fetch `Bought` logs within an optional block range (no shared full-history cache). */
export async function fetchBoughtLogsInBlockRange(
  fromBlock,
  toBlock,
  maxPages = 50,
  source = ETHERSCAN_GET_LOGS_SOURCE.ANALYTICS,
  walletAddress,
) {
  return fetchBoughtLogsAllPagesUncached(maxPages, fromBlock, toBlock, source, walletAddress);
}

/** Fetch vault winner logs within an optional block range (no shared full-history cache). */
export async function fetchVaultWinnerLogsInBlockRange(
  fromBlock,
  toBlock,
  maxPages = 50,
  source = ETHERSCAN_GET_LOGS_SOURCE.ANALYTICS,
  walletAddress,
) {
  return fetchLogsAllPagesWithRetry({
    address: coreVaultAddress,
    topic0: VAULT_WINNER_EVENT_TOPIC0,
    maxPages,
    fromBlock,
    toBlock,
    source,
    walletAddress,
    walletTopicIndex: walletAddress ? ETHERSCAN_WALLET_TOPIC_INDEX.VAULT_WINNER : undefined,
    label: 'vault winner getLogs',
  });
}

/** Fetch all `Bought` logs from Etherscan with no in-memory or localStorage cache. */
async function fetchBoughtLogsAllPagesUncached(
  maxPages = 50,
  fromBlock,
  toBlock,
  source = ETHERSCAN_GET_LOGS_SOURCE.ANALYTICS,
  walletAddress,
) {
  const chunks = await Promise.all(
    gameContractAddresses.map((address) =>
      fetchLogsAllPagesForAddress({
        address,
        topic0: BOUGHT_EVENT_TOPIC0,
        maxPages,
        fromBlock,
        toBlock,
        source,
        walletAddress,
        walletTopicIndex: walletAddress ? ETHERSCAN_WALLET_TOPIC_INDEX.BOUGHT_BUYER : undefined,
      }),
    ),
  );
  return chunks.flat();
}

/** Paginate Etherscan `getLogs` for every `Bought` event across all game contracts. */
export async function fetchBoughtLogsAllPages(
  maxPages = 50,
  source = ETHERSCAN_GET_LOGS_SOURCE.ANALYTICS,
  walletAddress,
) {
  if (walletAddress) {
    return fetchBoughtLogsAllPagesUncached(maxPages, undefined, undefined, source, walletAddress);
  }

  const now = Date.now();
  if (boughtLogsCache.logs && now - boughtLogsCache.fetchedAt < BOUGHT_LOGS_CACHE_TTL_MS) {
    return boughtLogsCache.logs;
  }

  const storedLogs = readStoredLogsCache(BOUGHT_LOGS_STORAGE_KEY, BOUGHT_LOGS_CACHE_TTL_MS);
  if (storedLogs) {
    boughtLogsCache = { logs: storedLogs, fetchedAt: Date.now() };
    return storedLogs;
  }

  if (boughtLogsInflight) return boughtLogsInflight;

  boughtLogsInflight = (async () => {
    try {
      const logs = await fetchBoughtLogsAllPagesUncached(maxPages, undefined, undefined, source);
      if (logs.length > 0) {
        const fetchedAt = Date.now();
        boughtLogsCache = { logs, fetchedAt };
        writeStoredLogsCache(BOUGHT_LOGS_STORAGE_KEY, logs);
      }
      return logs;
    } finally {
      boughtLogsInflight = null;
    }
  })();

  return boughtLogsInflight;
}

/**
 * Sum each log's amount (from `data`) and bucket by indexed address in `topics[1]`.
 * @returns {{ totalWei: import('ethers').BigNumber, byAddressWei: Record<string, string>, uniqueAddressCount: number }}
 */
export function sumAmountsByTopic1(logs) {
  const byAddress = new Map();
  for (const log of logs) {
    const addr = addressFromTopic(log.topics?.[1]);
    if (!addr) continue;
    const amount = uint256FromLogData(log.data);
    const prev = byAddress.get(addr) || BigNumber.from(0);
    byAddress.set(addr, prev.add(amount));
  }
  let totalWei = BigNumber.from(0);
  for (const v of byAddress.values()) {
    totalWei = totalWei.add(v);
  }
  const byAddressWei = Object.fromEntries(
    [...byAddress.entries()].map(([k, v]) => [k, v.toString()]),
  );
  return {
    totalWei,
    byAddressWei,
    uniqueAddressCount: byAddress.size,
  };
}

export async function fetchBoughtDataFromBackend() {
  const res = await fetch(GET_BOUGHT_DATA_API_URL);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `getBoughtData failed: ${res.status}`);
  }
  const json = await res.json();
  if (!Array.isArray(json?.data)) {
    throw new Error('getBoughtData: unexpected response');
  }
  return json.data;
}

function filterBoughtDataByDateRange(records, dateRange) {
  if (!dateRange) return records;
  const fromMs = parseFilterTimestampFrom(dateRange);
  const toMs = parseFilterTimestampTo(dateRange);
  if (fromMs == null && toMs == null) return records;

  return records.filter((record) => {
    const ts = Number(record?.timeStamp || 0) * 1000;
    if (fromMs != null && ts < fromMs) return false;
    if (toMs != null && ts > toMs) return false;
    return true;
  });
}

function computeGlobalStatsFromBoughtRecords(records) {
  const uniqueBuyers = new Set();
  let totalPrice = 0;

  for (const record of records) {
    const buyer = typeof record?.buyer === 'string' ? record.buyer.toLowerCase() : '';
    if (buyer) uniqueBuyers.add(buyer);
    totalPrice += Number(record?.price) || 0;
  }

  const pool = Math.max(0, Math.round(totalPrice));

  return {
    totalPlayers: uniqueBuyers.size,
    totalPrizesPaid: pool,
    totalTicketsSold: pool,
    currentJackpotPool: pool,
    totalDrawsCompleted: 156,
    activeAutoDrawEntries: 234,
    updatedAt: Date.now(),
  };
}

async function fetchGlobalStatsFromBackend({ dateRange } = {}) {
  try {
    const records = await fetchBoughtDataFromBackend();
    const filtered = filterBoughtDataByDateRange(records, dateRange);
    const stats = computeGlobalStatsFromBoughtRecords(filtered);

    const hasDateFilter = Boolean(dateRange?.dateFrom || dateRange?.dateTo);
    if (!hasDateFilter) {
      storage.set('global_stats', stats);
    }

    return stats;
  } catch (e) {
    console.error('getGlobalStats bought-data fetch failed:', e);
    return {
      totalPlayers: 0,
      totalPrizesPaid: 0,
      totalTicketsSold: 0,
      currentJackpotPool: 0,
      totalDrawsCompleted: 156,
      activeAutoDrawEntries: 234,
      updatedAt: Date.now(),
    };
  }
}

async function getSeededStats({ forceRefresh = false, dateRange } = {}) {
  const hasDateFilter = Boolean(dateRange?.dateFrom || dateRange?.dateTo);
  if (!hasDateFilter) {
    const cached = storage.get('global_stats');
    const isFresh = !forceRefresh && cached?.updatedAt && Date.now() - cached.updatedAt < GLOBAL_STATS_CACHE_TTL_MS;
    if (isFresh) return cached;
  }

  if (globalStatsInflight) return globalStatsInflight;

  globalStatsInflight = fetchGlobalStatsFromBackend({ dateRange }).finally(() => {
    globalStatsInflight = null;
  });
  return globalStatsInflight;
}

/** Clears cached global stats so the next `getGlobalStats()` refetches from the network. */
export function invalidateGlobalStatsCache() {
  storage.remove('global_stats');
  globalStatsInflight = null;
}

/** Clears all analytics Etherscan-derived caches. */
export function invalidateAllAnalyticsCache() {
  invalidateGlobalStatsCache();
  invalidateVaultWinnerLogsCache();
  invalidateBoughtLogsCache();
}

// Get global platform stats
export async function getGlobalStats(options = {}) {
  const { dateRange, forceRefresh } = options;
  return getSeededStats({ forceRefresh, dateRange });
}

/** Map one `getWinnerData` record to the Analytics Last Draw table row shape. */
function mapWinnerDataRecord(record) {
  const gameType = typeof record?.gameType === 'string' ? record.gameType.toLowerCase() : '';
  const gameName = GAMES[gameType]?.shortName || record?.gameType || 'Unknown';
  const roundId = record?.roundId != null ? String(record.roundId) : '';
  const walletAddress =
    typeof record?.winnerAddress === 'string' ? record.winnerAddress.toLowerCase() : '';
  const timestampMs = Number(record?.timeStamp || 0) * 1000;

  let winningNumberDisplay = '—';
  if (gameType && record?.winNumber != null && String(record.winNumber) !== '') {
    try {
      winningNumberDisplay = formatGameNumber(gameType, record.winNumber);
    } catch {
      winningNumberDisplay = String(record.winNumber);
    }
  }

  const txHash = record?.lastBoughtTransaction || record?.transactionHash || null;

  let prize = Number(record?.winnerAmount) || 0;
  if (prize > 1) {
    prize = prize * 0.9;
  }

  return {
    key: record?._id || `${txHash || 'winner'}-${record?.winNumber ?? ''}-${walletAddress}`,
    gameRoundLabel: formatGameRoundLabel(gameName, roundId),
    prize,
    winningNumberDisplay,
    walletAddress,
    userDisplay: walletAddress ? `${walletAddress.slice(0, 6)}…${walletAddress.slice(-4)}` : '—',
    txHash,
    vaultTxHash: record?.transactionHash || null,
    sortKey: timestampMs,
    timestampMs,
    blockHash: '',
  };
}

function filterLastDrawWinnersByDateRange(rows, dateRange) {
  if (!dateRange) return rows;
  const fromMs = parseFilterTimestampFrom(dateRange);
  const toMs = parseFilterTimestampTo(dateRange);
  if (fromMs == null && toMs == null) return rows;

  return rows.filter((row) => {
    const ts = row.timestampMs || 0;
    if (fromMs != null && ts < fromMs) return false;
    if (toMs != null && ts > toMs) return false;
    return true;
  });
}

export async function fetchWinnerDataFromBackend() {
  const res = await fetch(GET_WINNER_DATA_API_URL);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `getWinnerData failed: ${res.status}`);
  }
  const json = await res.json();
  if (!Array.isArray(json?.data)) {
    throw new Error('getWinnerData: unexpected response');
  }
  return json.data;
}

async function buildLastDrawWinnersRows(dateRange = null) {
  try {
    const records = await fetchWinnerDataFromBackend();
    const rows = records.map(mapWinnerDataRecord);
    rows.sort((a, b) => (b.timestampMs || b.sortKey) - (a.timestampMs || a.sortKey));
    return filterLastDrawWinnersByDateRange(rows, dateRange);
  } catch (e) {
    console.error(e);
    return [];
  }
}

export function paginateLastDrawWinnersRows(rows, page = 1, limit = 5) {
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * limit;

  return {
    winners: rows.slice(start, start + limit),
    total,
    totalPages,
    page: safePage,
    limit,
  };
}

/** All winner rows for the “Last Draw winner and number” analytics table (HashMedora backend). */
export async function fetchLastDrawWinnersRows(dateRange = null) {
  return buildLastDrawWinnersRows(dateRange);
}

/** Paginated winner rows for the “Last Draw winner and number” analytics table. */
export async function getLastDrawWinners(page = 1, limit = 5, dateRange = null) {
  const rows = await buildLastDrawWinnersRows(dateRange);
  return paginateLastDrawWinnersRows(rows, page, limit);
}

// Get win probability data for chart
export async function getWinProbabilityData() {
  return Object.values(GAMES).map((game) => ({
    name: game.shortName,
    gameId: game.id,
    icon: game.icon,
    probability: game.totalWinProbability * 100,
    directProbability: game.directWinProbability * 100,
    neighborProbability: game.neighborWinProbability * 100,
    maxPrize: game.maxPrize,
    entryFee: game.entryFee,
  }));
}

// Get game popularity stats
export async function getGamePopularity() {
  const bets = storage.getList('bets');
  const popularity = {};

  Object.keys(GAMES).forEach((gameId) => {
    popularity[gameId] = {
      gameId,
      gameName: GAMES[gameId].shortName,
      totalBets: 0,
      totalTickets: 0,
      totalPool: 0,
    };
  });

  bets.forEach((bet) => {
    if (popularity[bet.gameId]) {
      popularity[bet.gameId].totalBets += 1;
      popularity[bet.gameId].totalTickets += bet.numbers.length;
      popularity[bet.gameId].totalPool += bet.cost;
    }
  });

  return Object.values(popularity).sort((a, b) => b.totalBets - a.totalBets);
}

function formatSoldTickets(sold, gameId) {
  return formatSoldTicketsWithTotal(sold, gameId);
}

/** On-chain current round id and sold tickets per game (public view). */
export async function getCurrentRoundsOnChain() {
  const list = await Promise.all(
    GAME_IDS.map(async (gameId) => {
      const g = GAMES[gameId];
      const base = {
        gameId,
        gameName: g.shortName,
        gameIcon: g.icon,
        gameTextImage: g.textImage,
        roundId: '—',
        soldTickets: '—',
      };
      try {
        const gameTypeBytes32 = stringToHex(gameId, { size: 32 });
        const result = await readContract(config, {
          address: getGameContractAddress(gameId),
          abi: gameAbi,
          functionName: 'currentRounds',
          args: [gameTypeBytes32],
        });
        const [rid, sold] = result;
        return {
          ...base,
          roundId: rid?.toString?.() ?? String(rid),
          soldTickets: formatSoldTickets(sold, gameId),
        };
      } catch {
        return base;
      }
    }),
  );
  return list;
}

// Get next draw countdown for all games
export async function getNextDrawCountdowns() {
  const drawInterval = 3600000; // 1 hour
  const draws = storage.get('draws', {});

  return Object.keys(GAMES).map((gameId) => {
    const gameDraws = draws[gameId] || [];
    const latest = gameDraws[gameDraws.length - 1];
    const timeSince = latest ? Date.now() - latest.timestamp : drawInterval;
    const timeRemaining = Math.max(0, drawInterval - timeSince);

    return {
      gameId,
      gameName: GAMES[gameId].shortName,
      gameIcon: GAMES[gameId].icon,
      gameTextImage: GAMES[gameId].textImage,
      timeRemaining,
      nextDrawAt: Date.now() + timeRemaining,
    };
  });
}

const analyticsApi = {
  getCachedGlobalStats,
  getGlobalStats,
  getDefaultDateRange,
  getDefaultLastDrawDateRange,
  resolveBlockRangeFromDates,
  invalidateGlobalStatsCache,
  invalidateAllAnalyticsCache,
  fetchLastDrawWinnersRows,
  fetchBoughtLogsAllPages,
  paginateLastDrawWinnersRows,
  getLastDrawWinners,
  getWinProbabilityData,
  getGamePopularity,
  getNextDrawCountdowns,
  getCurrentRoundsOnChain,
};

export default analyticsApi;
